import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Send, AlertCircle, CheckCircle, Sparkles, FileText, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface GeminiResponse {
  choices: {
    message: { role: string; content: string };
    finish_reason: string;
  }[];
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  model: string;
  provider: string;
}

export default function TestlabGeminiPage() {
  const navigate = useNavigate();
  const [systemPrompt, setSystemPrompt] = useState("Du är en erfaren revisor som analyserar finansiell data.");
  const [userMessage, setUserMessage] = useState("Analysera följande data och identifiera potentiella avvikelser:\n\n2024-01-15: Moms in: 125,000 SEK\n2024-02-15: Moms in: 128,500 SEK\n2024-03-15: Moms in: 15,000 SEK (avvikande lågt)\n2024-04-15: Moms in: 131,200 SEK");
  const [responseFormat, setResponseFormat] = useState<'text' | 'json'>('text');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<GeminiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);

  async function sendRequest() {
    setLoading(true);
    setError(null);
    setResponse(null);
    setLatencyMs(null);
    
    const startTime = Date.now();
    
    try {
      const { data, error: fnError } = await supabase.functions.invoke('google-gemini-pro', {
        body: {
          messages: [{ role: 'user', content: userMessage }],
          systemPrompt: systemPrompt || undefined,
          temperature: 0.1,
          maxOutputTokens: 8192,
          responseFormat,
        }
      });

      setLatencyMs(Date.now() - startTime);

      if (fnError) {
        throw new Error(fnError.message);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setResponse(data);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  const estimatedTokens = Math.ceil((systemPrompt.length + userMessage.length) / 4);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      <div className="container mx-auto p-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/testlab")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka till Testlab
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Sparkles className="h-8 w-8 text-blue-500" />
              Google Gemini 1.5 Pro
            </h1>
            <p className="text-muted-foreground">
              Vertex AI med 1M tokens context window via Service Account
            </p>
          </div>
          <Badge variant="secondary" className="ml-auto">TESTLAB</Badge>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-700">Context Window</p>
                  <p className="text-2xl font-bold text-blue-900">1M tokens</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Zap className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-700">Provider</p>
                  <p className="text-2xl font-bold text-emerald-900">Vertex AI</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-purple-700">Autentisering</p>
                  <p className="text-lg font-bold text-purple-900">Service Account</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6">
          {/* Input Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Skicka fråga till Gemini 1.5 Pro
              </CardTitle>
              <CardDescription>
                Testar direkt kommunikation med Google Vertex AI via Service Account OAuth2.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="systemPrompt">System Prompt</Label>
                <Textarea
                  id="systemPrompt"
                  placeholder="Beskriv AI:ns roll..."
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={2}
                  className="resize-none font-mono text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="userMessage">Användarmeddelande</Label>
                <Textarea
                  id="userMessage"
                  placeholder="Skriv din fråga här..."
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  rows={8}
                  className="resize-none font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Estimerade tokens: ~{estimatedTokens.toLocaleString()}
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="space-y-2 flex-1">
                  <Label>Svarsformat</Label>
                  <Select value={responseFormat} onValueChange={(v) => setResponseFormat(v as 'text' | 'json')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text (fritext)</SelectItem>
                      <SelectItem value="json">JSON (strukturerad)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                onClick={sendRequest}
                disabled={loading || !userMessage.trim()}
                className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                <Sparkles className="h-4 w-4" />
                {loading ? "Skickar till Gemini..." : "Skicka till Gemini 1.5 Pro"}
              </Button>
            </CardContent>
          </Card>

          {/* Error Display */}
          {error && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="space-y-2 flex-1">
                    <p className="font-medium text-destructive">Fel uppstod</p>
                    <pre className="text-sm bg-destructive/10 p-3 rounded border text-destructive whitespace-pre-wrap overflow-x-auto">
                      {error}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Success Display */}
          {response && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <CheckCircle className="h-5 w-5" />
                  Svar från {response.model}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Metadata */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-blue-100/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-blue-700">Provider</p>
                    <p className="text-sm text-blue-600">{response.provider}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-700">Model</p>
                    <p className="text-sm text-blue-600">{response.model}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-700">Latency</p>
                    <p className="text-sm text-blue-600">{latencyMs?.toLocaleString()} ms</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-700">Tokens</p>
                    <p className="text-sm text-blue-600">
                      {response.usage?.total_tokens?.toLocaleString() || 'N/A'} 
                      {response.usage && ` (${response.usage.prompt_tokens}+${response.usage.completion_tokens})`}
                    </p>
                  </div>
                </div>

                {/* Response Content */}
                <div>
                  <Label className="text-blue-700 font-medium">Svar:</Label>
                  <div className="mt-2 p-4 bg-white border border-blue-200 rounded-lg max-h-[500px] overflow-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono">
                      {response.choices?.[0]?.message?.content || 'Inget svar'}
                    </pre>
                  </div>
                </div>

                {/* Finish Reason */}
                {response.choices?.[0]?.finish_reason && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-blue-600 border-blue-300">
                      Finish reason: {response.choices[0].finish_reason}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Comparison Card */}
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="text-sm text-muted-foreground">Jämförelse: Gemini vs Azure</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p className="font-medium">Google Gemini 1.5 Pro</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>✅ 1M tokens context</li>
                    <li>✅ Multimodal (bilder, video)</li>
                    <li>✅ Service Account auth</li>
                    <li>⚡ Bra för stora dataset</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <p className="font-medium">Azure OpenAI (GPT-4.1)</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>✅ 128K tokens context</li>
                    <li>✅ Snabb inferens</li>
                    <li>✅ API key auth</li>
                    <li>⚡ Bra för mindre analyser</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
